import "./dashboard-sections.css";
import DashboardCard from "./DashboardCard";
import { makePriorLotNumber } from "../../../helpers/Utils";
import { useGetProcessingAnalyticsQuery } from "../dashBoardApi";
import { useEffect } from "react";

const MetricBox = ({ label, value, unit, isLoading = false }) => (
  <div className="db-evap-box">
    <div className="db-evap-label">{label}</div>
    <div className="db-evap-value neutral">
      {isLoading
        ? <span className="db-shimmer" style={{ height: 24, width: 60, display: "block" }} />
        : <>{value ?? "—"}{unit && <span className="db-metric-unit"> {unit}</span>}</>
      }
    </div>
  </div>
);

const BatchBadge = ({ startDate, endDate }) => (
  <span className="pko-batch-badge">
    #{makePriorLotNumber(startDate)} - #{makePriorLotNumber(endDate)}
  </span>
);

const ProcessingOverview = ({ startDate, endDate }) => {

  const {
    data: procsData,
    isLoading: isProcsDataLoading,
    refetch:processingRefetch
  } = useGetProcessingAnalyticsQuery(
    {
      startDate,
      endDate,
      batchFrom : makePriorLotNumber(startDate),
      batchTo : makePriorLotNumber(endDate),
    },
    {
      skip: !startDate || !endDate // avoid API call if empty
    }
  );


  useEffect(() => {
    processingRefetch();
  }, []);

  const takenOffData = procsData?.data?.kg_taken_off ?? [] ;
  const trashData = procsData?.data?.trash ?? [] ;
  const treysData = procsData?.data?.treys ?? [] ;
  const procsCasesData = procsData?.data?.cases ?? [] ;

  return (
    <div className="po-wrapper">
      {/* CASES — full width */}
      <DashboardCard
        title="Cases"
        badgeIcon={startDate && endDate ? <BatchBadge startDate={startDate} endDate={endDate} /> : null}
      >
        <div className="db-evap-row">
          <MetricBox label="CASES ENTERED"    value={procsCasesData?.cases_entered ?? 0}          isLoading={isProcsDataLoading} />
          <MetricBox label="CASES PROCESSED"  value={procsCasesData?.cases_processed ?? 0}        isLoading={isProcsDataLoading} />
          <MetricBox label="CASES COMBINED"   value={procsCasesData?.cases_combined ?? 0}         isLoading={isProcsDataLoading} />
          <MetricBox label="PEELERS"          value={procsCasesData?.number_of_peelers ?? 0}      isLoading={isProcsDataLoading} />
        </div>
      </DashboardCard>

      {/* Bottom row */}
      {/* <div className="pf-live-dot"> </div> */}
      <div className="po-bottom-row-3">

        {/* Left: Treys & Carts */}
        <DashboardCard 
          title="Treys &amp; Carts"
          badgeIcon={startDate && endDate ? <BatchBadge startDate={startDate} endDate={endDate} /> : null}
        >
          <div className="po-treys-grid">
            <MetricBox label="TREYS STRIPS" value={treysData?.striped_treys ?? 0}  isLoading={isProcsDataLoading} />
            <MetricBox label="TREYS CHEEKS" value={treysData?.cheeks_treys ?? 0}   isLoading={isProcsDataLoading} />
            <MetricBox label="TREYS TAPITA" value={treysData?.tapitas_treys ?? 0}  isLoading={isProcsDataLoading} />
            <MetricBox label="TOTAL TREYS"  value={
                (treysData?.striped_treys ?? 0) +
                (treysData?.cheeks_treys ?? 0) +
                (treysData?.tapitas_treys ?? 0)
              }
              isLoading={isProcsDataLoading}
            />
            <MetricBox label="CARTS" value={treysData?.carts ?? 0} isLoading={isProcsDataLoading} />
          </div>
        </DashboardCard>

        {/* Right: KG Taken Off + Trash — separate cards stacked vertically */}
        <div className="po-right-col">
          <DashboardCard
            title="KG Taken Off"
            icon={<span className="po-total-kg-badge">Total: {takenOffData?.total_taken_off_kg ?? "0"} kg</span>}
            badgeIcon={startDate && endDate ? <BatchBadge startDate={startDate} endDate={endDate} /> : null}
          >
            <div className="db-avg-row">
              <span>Strip Total</span>
              <span className="db-avg-val">
                {isProcsDataLoading ? <span className="db-shimmer" style={{ height: 16, width: 60, display: "inline-block" }} /> : <>{takenOffData?.total_strips_kg ?? "0"} <span className="db-metric-unit">kg</span></>}
              </span>
            </div>
            <div className="db-avg-row">
              <span>Cheek Total</span>
              <span className="db-avg-val">
                {isProcsDataLoading ? <span className="db-shimmer" style={{ height: 16, width: 60, display: "inline-block" }} /> : <>{takenOffData?.total_cheeks_kg ?? "0"} <span className="db-metric-unit">kg</span></>}
              </span>
            </div>
            <div className="db-avg-row">
              <span>Tapita Total</span>
              <span className="db-avg-val">
                {isProcsDataLoading ? <span className="db-shimmer" style={{ height: 16, width: 60, display: "inline-block" }} /> : <>{takenOffData?.total_tapita_kg ?? "0"} <span className="db-metric-unit">kg</span></>}
              </span>
            </div>
          </DashboardCard>

          <DashboardCard 
            title="Trash"
            badgeIcon={startDate && endDate ? <BatchBadge startDate={startDate} endDate={endDate} /> : null}
          >
            <div className="db-avg-row">
              <span>Trash (Sorting)</span>
              <span className="db-avg-val">
                {isProcsDataLoading ? <span className="db-shimmer" style={{ height: 16, width: 60, display: "inline-block" }} /> : <>{trashData?.trash_kg_sorting ?? "0"} <span className="db-metric-unit">kg</span></>}
              </span>
            </div>
            <div className="db-avg-row">
              <span>Trash (Processing)</span>
              <span className="db-avg-val">
                {isProcsDataLoading ? <span className="db-shimmer" style={{ height: 16, width: 60, display: "inline-block" }} /> : <>{trashData?.trash_kg_processing ?? "0"} <span className="db-metric-unit">kg</span></>}
              </span>
            </div>
          </DashboardCard>
        </div>

      </div>
    </div>
  );
};

export default ProcessingOverview;
